import React, { useEffect, useMemo, useState } from 'react';
import AvatarImage from './AvatarImage';
import ChatBubbleIcon from './icons/ChatBubbleIcon';
import XIcon from './icons/XIcon';
import type { Language } from '../types';
import { getTranslation } from '../utils/translations';
import { formatRelativeTime } from '../utils/timeAgo';
import type { ConsultantBannerViewModel } from '../hooks/useConsultantBannerData';
import { getInitials } from '../hooks/useConsultantBannerData';
import {
    dismissQuoteBubbleForToday,
    shouldShowQuoteBubble,
} from '../utils/consultantQuoteVisibility';

interface ConsultantBannerProps {
    data: ConsultantBannerViewModel;
    language?: Language;
    onOpenChat: () => void;
}

const ConsultantBanner: React.FC<ConsultantBannerProps> = ({ data, language, onOpenChat }) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(language, key);
    const [isBubbleVisible, setIsBubbleVisible] = useState(false);
    const initials = useMemo(() => getInitials(data.name), [data.name]);

    const hasPhrase = Boolean(data.phraseOfTheDay?.trim());

    useEffect(() => {
        setIsBubbleVisible(shouldShowQuoteBubble(data.consultantId, hasPhrase));
    }, [data.consultantId, data.phraseOfTheDay, hasPhrase]);

    const planUpdatedLabel = data.lastPlanUpdateAt
        ? `${t('planUpdated')}: ${formatRelativeTime(data.lastPlanUpdateAt, language)}`
        : null;

    const handleDismissBubble = () => {
        dismissQuoteBubbleForToday(data.consultantId);
        setIsBubbleVisible(false);
    };

    return (
        <section className="relative z-40 px-4 py-2 bg-brand-sage/[0.05] dark:bg-brand-sage/10 border-b border-brand-sage/10 dark:border-brand-sage/20">
            <div className="flex flex-row items-center justify-between gap-3 min-h-[2.75rem]">
                <AvatarImage
                    src={data.avatarUrl}
                    alt={data.name}
                    className="h-9 w-9 rounded-full object-cover border-2 border-white dark:border-slate-800 shadow-sm bg-white dark:bg-slate-800 shrink-0"
                    fallback={
                        <div
                            className="h-9 w-9 rounded-full bg-brand-sage/15 text-brand-sage dark:bg-brand-sage/25 dark:text-brand-sage flex items-center justify-center text-[10px] font-black border-2 border-white dark:border-slate-800 shrink-0"
                            aria-hidden
                        >
                            {initials}
                        </div>
                    }
                />

                <div className="min-w-0 flex-1">
                    <p className="text-sm font-black text-slate-800 dark:text-slate-100 truncate leading-tight">
                        {data.name}
                    </p>
                    <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 truncate">
                        {data.roleLabel}
                    </p>
                    {planUpdatedLabel && (
                        <p className="text-xs text-slate-400 dark:text-slate-500 truncate leading-tight mt-0.5">
                            {planUpdatedLabel}
                        </p>
                    )}
                </div>

                <button
                    type="button"
                    onClick={onOpenChat}
                    className="relative shrink-0 h-9 w-9 rounded-xl bg-brand-sage text-white shadow-md shadow-brand-sage/25 hover:bg-slate-800 dark:hover:bg-slate-700 active:scale-95 transition-all flex items-center justify-center self-center"
                    aria-label={t('consultationBox')}
                >
                    <ChatBubbleIcon className="w-[1.15rem] h-[1.15rem]" />
                    {data.hasUnreadMessages && (
                        <span className="absolute top-1 end-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white dark:ring-slate-900" />
                    )}
                </button>
            </div>

            {isBubbleVisible && data.phraseOfTheDay && (
                <div className="absolute top-[calc(100%+0.35rem)] start-3 z-50 max-w-[min(18rem,calc(100%-1.5rem))]">
                    <div className="relative bg-white dark:bg-slate-900 rounded-2xl px-3.5 py-3 pe-8 shadow-lg shadow-slate-900/10 dark:shadow-black/30 border border-slate-100 dark:border-slate-700">
                        <span
                            className="absolute -top-1.5 start-5 h-3 w-3 rotate-45 bg-white dark:bg-slate-900 border-s border-e border-slate-100 dark:border-slate-700"
                            aria-hidden
                        />
                        <button
                            type="button"
                            onClick={handleDismissBubble}
                            className="absolute top-1.5 end-1.5 p-0.5 rounded-full text-slate-300 hover:text-slate-500 dark:text-slate-600 dark:hover:text-slate-300 transition-colors"
                            aria-label={t('close')}
                        >
                            <XIcon className="w-3.5 h-3.5" />
                        </button>
                        <p className="text-xs italic text-slate-600 dark:text-slate-300 leading-snug relative z-10">
                            “{data.phraseOfTheDay}”
                        </p>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ConsultantBanner;
